import { defineStore } from "pinia";
import App from "@bloglovin/inspire-engine/App";
import { useGeneralStore } from "%/dynamic-component-editor/stores/general-store";
import { contextualAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import {
  FOLDER_COUNT_KEYS,
  folderCountsFrom,
} from "%/dynamic-component-editor/standard-components/base-story-card/completion-progress-percent";
import { COMPLETION_NOT_STARTED } from "%/dynamic-component-editor/constants/completion-status-values";
import {
  addOverlay,
  ancestorOverlayDeltas,
  applyOverlay,
  isEmptyOverlay,
} from "%/dynamic-component-editor/helpers/completion-optimistic-overlay";

const STORY_ENTITY_TYPE = "Story";
const QUEUE_WRITE = "write";
const QUEUE_READ = "read";

function folderIdOf(reference) {
  if (reference && typeof reference === "object") {
    return Number(reference.id ?? reference.pk ?? reference.storyId) || 0;
  }
  return Number(reference) || 0;
}

const parentFolderByStory = new Map();

function serverMessage(error) {
  return error?.response?.data?.message || error?.message || "no response";
}

export {
  COMPLETION_NOT_STARTED,
  COMPLETION_STARTED,
  COMPLETION_COMPLETE,
} from "%/dynamic-component-editor/constants/completion-status-values";

function normalizeFolderProgress(src) {
  return {
    total: Number(src?.total) || 0,
    completed: Number(src?.completed) || 0,
    percent: src?.percent != null ? Number(src.percent) : null,
    ...folderCountsFrom(src),
  };
}

function presentCounts(counts) {
  return Object.fromEntries(Object.entries(counts).filter(([, value]) => value !== null));
}

export const useCompletionStatusStore = defineStore("completion-status-store", {
  state: () => ({
    statusByStory: {},
    progressByFolder: {},
    serverProgressByFolder: {},
    pendingOverlayByFolder: {},
    completionQueue: [],
    completionInFlight: false,
    completionSeq: 0,
    foldersToRefetchOnDrain: {},
  }),
  getters: {
    hasSignedInUser() {
      return useGeneralStore().isLoggedIn;
    },
    folderCountsFor() {
      return (folderId, record) => ({ ...folderCountsFrom(record), ...presentCounts(this.progressByFolder[folderId] ?? {}) });
    },
  },
  actions: {
    applyCompletionStatus(storyId, completionStatus) {
      if (!storyId || !completionStatus) {
        return;
      }
      this.statusByStory[storyId] = completionStatus;
      this.syncStoryEntityCompletionStatus(storyId, completionStatus);
    },
    syncStoryEntityCompletionStatus(storyId, completionStatus) {
      this.hydrateStoryEntity(storyId, { completionStatus });
    },
    hydrateStoryEntity(storyId, fields) {
      const appName = contextualAppName();
      if (!App.getAllBootedInstances()[appName]) {
        return;
      }
      const entity = App.get(appName).getEntity?.(STORY_ENTITY_TYPE, { id: String(storyId) });
      if (!entity?.getFieldData?.() || Object.keys(fields).length === 0) {
        return;
      }
      try {
        entity.hydrateFields(fields, true);
      } catch (error) {
        console.error(`[completion-status-store] could not update Story ${storyId} (${Object.keys(fields).join(", ")}): ${error.message}; expressions bound to them stay stale`); // eslint-disable-line no-console
      }
    },
    applyFolderCounts(folderId, src) {
      const missing = Object.values(FOLDER_COUNT_KEYS).filter(key => src?.[key] === undefined);
      if (missing.length) {
        console.error(`[completion-status-store] folder ${folderId}: content sent no ${missing.join(", ")}, so those counts stay unknown (content needs task 129, #550)`); // eslint-disable-line no-console
      }
      this.serverProgressByFolder[folderId] = normalizeFolderProgress(src);
      this.showFolderProgress(folderId);
    },
    showFolderProgress(folderId) {
      const baseline = this.serverProgressByFolder[folderId];
      if (!baseline) {
        return;
      }
      const shown = applyOverlay(baseline, this.pendingOverlayByFolder[folderId]);
      this.progressByFolder[folderId] = shown;
      this.hydrateStoryEntity(folderId, presentCounts(folderCountsFrom(shown)));
    },
    addFolderOverlays(deltas, sign) {
      for (const [folderId, delta] of deltas) {
        const overlay = addOverlay(this.pendingOverlayByFolder[folderId], delta, sign);
        if (isEmptyOverlay(overlay)) {
          delete this.pendingOverlayByFolder[folderId];
        } else {
          this.pendingOverlayByFolder[folderId] = overlay;
        }
        this.showFolderProgress(folderId);
      }
    },
    rememberParentFolder(storyId, folderId) {
      const parentId = folderIdOf(folderId);
      if (Number(storyId) > 0 && parentId > 0) {
        parentFolderByStory.set(Number(storyId), parentId);
      }
    },
    parentFolderOf(storyId) {
      if (parentFolderByStory.has(Number(storyId))) {
        return parentFolderByStory.get(Number(storyId));
      }
      const appName = contextualAppName();
      if (!App.getAllBootedInstances()[appName]) {
        return 0;
      }
      const data = App.get(appName).getEntity?.(STORY_ENTITY_TYPE, { id: String(storyId) })?.getFieldData?.();
      return folderIdOf(data?.myFolder);
    },
    folderChainOf(storyId) {
      const chain = [];
      const seen = new Set([Number(storyId)]);
      let folderId = this.parentFolderOf(storyId);
      while (folderId > 0 && !seen.has(folderId)) {
        seen.add(folderId);
        chain.push({ folderId, counts: this.serverProgressByFolder[folderId] ? this.progressByFolder[folderId] : null });
        folderId = this.parentFolderOf(folderId);
      }
      return chain;
    },
    currentStatusOf(storyId) {
      if (this.statusByStory[storyId]) {
        return this.statusByStory[storyId];
      }
      const appName = contextualAppName();
      const entity = App.getAllBootedInstances()[appName]
        ? App.get(appName).getEntity?.(STORY_ENTITY_TYPE, { id: String(storyId) })
        : null;
      return entity?.getFieldData?.()?.completionStatus || COMPLETION_NOT_STARTED;
    },
    setCompletionStatus(storyId, completionStatus, parentFolderId = 0) {
      if (!storyId || !this.hasSignedInUser) {
        return;
      }
      this.rememberParentFolder(storyId, parentFolderId);
      const from = this.currentStatusOf(storyId);
      this.statusByStory[storyId] = completionStatus;
      this.syncStoryEntityCompletionStatus(storyId, completionStatus);
      if (from === completionStatus) {
        return;
      }
      const deltas = ancestorOverlayDeltas({ from, to: completionStatus, chain: this.folderChainOf(storyId) });
      this.addFolderOverlays(deltas, 1);
      return this.enqueueCompletion({ kind: QUEUE_WRITE, storyId, from, to: completionStatus, deltas });
    },
    enqueueCompletion(item) {
      return new Promise(resolve => {
        this.completionSeq += 1;
        this.completionQueue.push({ ...item, seq: this.completionSeq, resolve });
        this.sendNextCompletion();
      });
    },
    sendNextCompletion() {
      if (this.completionInFlight) {
        return;
      }
      const item = this.completionQueue[0];
      if (!item) {
        this.refetchFoldersLeftStale();
        return;
      }
      this.completionInFlight = true;
      const finish = () => {
        this.completionQueue.shift();
        this.completionInFlight = false;
        item.resolve();
        this.sendNextCompletion();
      };
      if (item.kind === QUEUE_READ) {
        this.apiGet(
          `/story/${item.folderId}/completion-progress`,
          {},
          progress => this.settle(item, finish, () => this.applyFolderCounts(item.folderId, progress)),
          error => {
            const reason = serverMessage(error);
            console.error(`[completion-status-store] could not load progress for folder ${item.folderId}: ${reason}; its bar keeps its last value`); // eslint-disable-line no-console
            finish();
          },
        );
        return;
      }
      this.apiPost(
        `/story/${item.storyId}/completion-status`,
        { completion_status: item.to },
        result => this.settle(item, finish, () => this.confirmCompletion(item, result)),
        error => {
          this.rollBackCompletion(item, serverMessage(error));
          finish();
        },
      );
    },
    settle(item, finish, apply) {
      try {
        apply();
      } catch (error) {
        const subject = item.kind === QUEUE_READ ? `folder ${item.folderId}` : `story ${item.storyId}`;
        console.error(`[completion-status-store] applying the server's answer for ${subject} failed: ${error.message}; progress may be stale`); // eslint-disable-line no-console
      }
      finish();
    },
    laterWritesFor(item) {
      return this.completionQueue.filter(queued => queued !== item && queued.kind === QUEUE_WRITE && queued.storyId === item.storyId);
    },
    confirmCompletion(item, result) {
      this.addFolderOverlays(item.deltas, -1);
      const refreshed = new Set();
      for (const folder of Array.isArray(result?.affected_folders) ? result.affected_folders : []) {
        const folderId = Number(folder?.folder_id) || 0;
        if (!folderId) {
          continue;
        }
        refreshed.add(folderId);
        this.applyFolderCounts(folderId, folder);
        if (folder?.status && !this.pendingOverlayByFolder[folderId]) {
          this.applyCompletionStatus(folderId, folder.status);
        }
      }
      for (const folderId of item.deltas.keys()) {
        if (!refreshed.has(folderId)) {
          this.foldersToRefetchOnDrain[folderId] = true;
        }
      }
      if (this.laterWritesFor(item).length === 0) {
        this.applyCompletionStatus(item.storyId, result?.completion_status ?? item.to);
      }
    },
    rollBackCompletion(item, reason) {
      this.addFolderOverlays(item.deltas, -1);
      const later = this.laterWritesFor(item);
      const next = later[0];
      if (next) {
        this.addFolderOverlays(next.deltas, -1);
        next.from = item.from;
        next.deltas = ancestorOverlayDeltas({ from: next.from, to: next.to, chain: this.folderChainOf(next.storyId) });
        this.addFolderOverlays(next.deltas, 1);
      } else {
        this.applyCompletionStatus(item.storyId, item.from);
      }
      const what = `marking story ${item.storyId} ${item.to} failed (${reason})`;
      console.error(`[completion-status-store] ${what}; it shows ${item.from} again and its folders' progress was rolled back`); // eslint-disable-line no-console
    },
    refetchFoldersLeftStale() {
      const folderIds = Object.keys(this.foldersToRefetchOnDrain).map(Number);
      this.foldersToRefetchOnDrain = {};
      for (const folderId of folderIds) {
        this.fetchFolderProgress(folderId);
      }
    },
    applyAffectedFolders(folders) {
      if (!Array.isArray(folders)) {
        return;
      }
      for (const folder of folders) {
        const folderId = Number(folder?.folder_id) || 0;
        if (!folderId) {
          continue;
        }
        this.applyFolderCounts(folderId, folder);
        if (folder?.status) {
          this.applyCompletionStatus(folderId, folder.status);
        }
      }
    },
    ensureFolderProgress(folderStoryId) {
      if (!folderStoryId || folderStoryId in this.progressByFolder) {
        return;
      }
      if (!this.hasSignedInUser) {
        return;
      }
      this.progressByFolder[folderStoryId] = { total: 0, completed: 0, leafNodesInTree: 0, completedLeafNodesInTree: 0 };
      return this.fetchFolderProgress(folderStoryId);
    },
    fetchFolderProgress(folderStoryId) {
      if (!folderStoryId) {
        return;
      }
      return this.enqueueCompletion({ kind: QUEUE_READ, folderId: folderStoryId });
    },
  },
});
